/**
 * The screener bar's pure half (`docs/plans/screener-bar.md`).
 *
 * What the two screen buttons hold, how the ▾ list is grouped, what the ◎
 * button says, and whether the bar stacks. Everything here is decidable
 * without a layout engine, which is the reason it is not in the component:
 * jsdom measures nothing, so the rules have to be arithmetic over inputs.
 */

import type { SymbolGroup, SymbolGroupType } from "../api/symbols";
import type { GroupSelection } from "./group-selection";
import type { ScreenerPlacement } from "./layout-templates";
import { isOwn, orderTabs, type Screen } from "./screens";

/** The shorter of a group's code and its name — what a chip shows. */
export function chipLabel(g: { code: string; name: string }): string {
  return g.name.length < g.code.length ? g.name : g.code;
}

// ── Screens ───────────────────────────────────────────────────────────────

/** One heading in the ▾ list and the screens under it. */
export interface ScreenSection {
  title: "Special" | "Group screens" | "Mine";
  screens: Screen[];
}

/**
 * The ▾ list, grouped as the mock has it: Special (the trade lists), the
 * group's screens (a club's or the product's), then yours. Empty headings are
 * dropped. Within each, the strip's own order (`orderTabs`).
 */
export function screenSections(screens: readonly Screen[]): ScreenSection[] {
  const tabs = orderTabs(screens);
  const sections: ScreenSection[] = [
    { title: "Special", screens: tabs.filter((s) => s.recs) },
    { title: "Group screens", screens: tabs.filter((s) => !s.recs && !isOwn(s)) },
    { title: "Mine", screens: tabs.filter((s) => !s.recs && isOwn(s)) },
  ];
  return sections.filter((s) => s.screens.length > 0);
}

/**
 * The two screens on the bar.
 *
 * `current` is the panel's `screenRef`, or the strip's fallback when it has
 * none. `previous` is what was current before it; when that is unknown or gone
 * from the catalog the next screen in strip order stands in, so the bar always
 * shows two whenever two exist.
 */
export function screenPair(
  screens: readonly Screen[],
  current: string | undefined,
  previous: string | undefined,
): { current?: Screen; previous?: Screen } {
  const tabs = orderTabs(screens);
  const cur = tabs.find((s) => s.key === current) ?? tabs[0];
  const prev =
    (previous !== cur?.key ? tabs.find((s) => s.key === previous) : undefined) ??
    tabs.find((s) => s.key !== cur?.key);
  return { current: cur, previous: prev };
}

const PREVIOUS_KEY = "qufolio.screener.previous";

/**
 * The previous screen, per section — **per browser**, beside the group
 * selection (`group-selection.ts`), which is where the panel's picked groups
 * and their off flags already live. Provisional (plan, decision 1): making it
 * follow the account is a contract field and a qv-data column.
 */
export function recallPrevious(sectionId: string): string | undefined {
  try {
    const all = JSON.parse(window.localStorage.getItem(PREVIOUS_KEY) ?? "{}") as Record<string, unknown>;
    const value = all[sectionId];
    return typeof value === "string" ? value : undefined;
  } catch {
    return undefined;
  }
}

export function rememberPrevious(sectionId: string, key: string): void {
  try {
    const all = JSON.parse(window.localStorage.getItem(PREVIOUS_KEY) ?? "{}") as Record<string, unknown>;
    all[sectionId] = key;
    window.localStorage.setItem(PREVIOUS_KEY, JSON.stringify(all));
  } catch {
    // Storage unavailable — the bar falls back to the next screen in order.
  }
}

// ── Recent screens (owner, 2026-10-16) ───────────────────────────────────

const RECENT_KEY = "qufolio.screener.recent";
/** How many a panel remembers — more than it shows, so a wider bar has some. */
const RECENT_KEEP = 5;

/**
 * The panel's recent screens, newest first, **per panel** (owner), per browser
 * like the picked groups. A panel that only ever had a remembered *previous*
 * screen reads it as its one recent screen.
 */
export function recallRecent(sectionId: string): string[] {
  try {
    const all = JSON.parse(window.localStorage.getItem(RECENT_KEY) ?? "{}") as Record<string, unknown>;
    const list = all[sectionId];
    if (Array.isArray(list)) return list.filter((k): k is string => typeof k === "string");
  } catch {
    // fall through to the older single value
  }
  const previous = recallPrevious(sectionId);
  return previous ? [previous] : [];
}

/** `key` becomes the most recent; the list is stored and returned. */
export function rememberRecent(sectionId: string, key: string): string[] {
  const next = [key, ...recallRecent(sectionId).filter((k) => k !== key)].slice(0, RECENT_KEEP);
  try {
    const all = JSON.parse(window.localStorage.getItem(RECENT_KEY) ?? "{}") as Record<string, unknown>;
    all[sectionId] = next;
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(all));
  } catch {
    // Storage unavailable — the bar falls back to the next screens in order.
  }
  rememberPrevious(sectionId, key);
  return next;
}

const CURRENT_KEY = "qufolio.screener.current";

/**
 * The screen a panel last showed, per panel, per browser — so a **remounted**
 * panel still sees the change. Switching to or from a TopDown swaps
 * `ScreenerView` for `TopDownView` (and back), and a fresh view that took its
 * own first screen as the starting point never recorded the one before it:
 * Impulse ↔ TopDown left the bar's second button on whatever came next in
 * strip order (owner, 2026-10-17).
 */
export function recallCurrent(sectionId: string): string | undefined {
  try {
    const all = JSON.parse(window.localStorage.getItem(CURRENT_KEY) ?? "{}") as Record<string, unknown>;
    const value = all[sectionId];
    return typeof value === "string" ? value : undefined;
  } catch {
    return undefined;
  }
}

export function rememberCurrent(sectionId: string, key: string): void {
  try {
    const all = JSON.parse(window.localStorage.getItem(CURRENT_KEY) ?? "{}") as Record<string, unknown>;
    if (all[sectionId] === key) return;
    all[sectionId] = key;
    window.localStorage.setItem(CURRENT_KEY, JSON.stringify(all));
  } catch {
    // Storage unavailable — a remount forgets the screen before, as it did.
  }
}

/**
 * The screen buttons the bar shows: the current one, then the recent ones that
 * still exist, newest first — `count` in all, topped up from the strip order
 * when there are not enough recent ones.
 */
export function recentScreens(
  screens: readonly Screen[],
  current: string | undefined,
  recent: readonly string[],
  count: number,
): Screen[] {
  const tabs = orderTabs(screens);
  const cur = tabs.find((s) => s.key === current) ?? tabs[0];
  if (!cur) return [];
  const out: Screen[] = [cur];
  for (const key of recent) {
    if (out.length >= count) break;
    const s = tabs.find((x) => x.key === key);
    if (s && !out.includes(s)) out.push(s);
  }
  for (const s of tabs) {
    if (out.length >= count) break;
    if (!out.includes(s)) out.push(s);
  }
  return out;
}

/** From this width a wide bar shows three screens rather than two. */
export const THREE_SCREENS_FROM = 1400;

// ── Universe ──────────────────────────────────────────────────────────────

/**
 * A selection as the working set — the command search's "select groups", which
 * builds one. The bar itself now has both modes again (below).
 */
export function asWorkingSet(selection: GroupSelection): GroupSelection {
  if (selection.view !== "single") return selection;
  return {
    ...selection,
    view: "multi",
    picked: selection.group
      ? [{ groupType: selection.groupType, code: selection.group, on: true }]
      : selection.picked,
  };
}

/*
 * Single / Multiple, back in the ◎ menu (owner, 2026-10-27 — reverses the plan's
 * decision 2). Both modes share the chips (`picked`). Multiple switches each on
 * or off; Single has **exactly one on** — `group` names it — so clicking a chip
 * moves the screen to that group alone. Single never writes `on`, so switching
 * back to Multiple restores its own choices.
 */

const same = (p: { groupType: string; code: string }, groupType: string, code: string) =>
  p.groupType === groupType && p.code === code;

/** The chips the bar draws: in Single, every chip with only the chosen one on. */
export function barPicked(selection: GroupSelection): GroupSelection["picked"] {
  if (selection.view !== "single") return selection.picked;
  return selection.picked.map((p) => ({ ...p, on: same(p, selection.groupType, selection.group ?? "") }));
}

/** Single: a ticked or clicked group becomes the one that is on, as a chip if it was not one. */
export function pickSingle(selection: GroupSelection, groupType: string, code: string): GroupSelection {
  const picked = selection.picked.some((p) => same(p, groupType, code))
    ? selection.picked
    : [...selection.picked, { groupType, code, on: true }];
  return { ...selection, view: "single", picked, groupType, group: code };
}

/** Single: remove a chip; if it was the one on, the next chip takes its place. */
export function removeSingle(selection: GroupSelection, groupType: string, code: string): GroupSelection {
  const picked = selection.picked.filter((p) => !same(p, groupType, code));
  if (selection.group !== code || selection.groupType !== groupType) return { ...selection, picked };
  const next = picked[0];
  return next
    ? { ...selection, picked, groupType: next.groupType, group: next.code }
    : { ...selection, picked, group: null };
}

/**
 * Switch mode. Single opens on the group it last had while that is still a
 * chip, else the first chip that is on, else the first chip.
 */
export function withView(selection: GroupSelection, view: GroupSelection["view"]): GroupSelection {
  if (view === "multi") return { ...selection, view };
  const { group, groupType, picked } = selection;
  if (group && picked.some((p) => same(p, groupType, group))) return { ...selection, view };
  const first = picked.find((p) => p.on) ?? picked[0];
  return first
    ? { ...selection, view, groupType: first.groupType, group: first.code }
    : { ...selection, view, group: null };
}

function findGroup(
  groupsByType: ReadonlyMap<string, readonly SymbolGroup[]>,
  groupType: string,
  code: string,
): SymbolGroup | undefined {
  return (groupsByType.get(groupType) ?? []).find((g) => g.code === code);
}

/**
 * How many symbols the groups that are **on** hold (owner, 2026-10-16), or
 * null when that sum is not the answer: under AND with two or more on, the
 * intersection is smaller and nothing cheaper than the screen knows by how much.
 */
export function onCount(
  selection: GroupSelection,
  groupsByType: ReadonlyMap<string, readonly SymbolGroup[]>,
): number | null {
  const on = selection.picked.filter((p) => p.on);
  if (on.length === 0) return 0;
  if (selection.combine === "and" && on.length > 1) return null;
  return on.reduce((sum, p) => sum + (findGroup(groupsByType, p.groupType, p.code)?.memberCount ?? 0), 0);
}

/**
 * What the ◎ button says — the table in the plan.
 *
 * `folded` is the wide bar of a screen with settings: the chips are not shown,
 * so the button has to carry the universe in words, and only what is on.
 * Otherwise the chips are beside it and the button names the kind.
 */
export function universeLabel(
  selection: GroupSelection,
  groupsByType: ReadonlyMap<string, readonly SymbolGroup[]>,
  types: readonly SymbolGroupType[],
  folded: boolean,
): string {
  const picked = selection.picked;
  if (picked.length === 0) return "Pick a universe";
  const kindLabel = (t: string) => types.find((x) => x.groupType === t)?.label ?? t;
  if (!folded) {
    const kinds = [...new Set(picked.map((p) => p.groupType))];
    return kinds.length === 1 ? kindLabel(kinds[0]!) : `${kinds.length} kinds`;
  }
  const on = picked.filter((p) => p.on);
  if (on.length === 0) return "Nothing on";
  const count = onCount(selection, groupsByType);
  const tail = count === null ? "" : ` · ${count.toLocaleString("en-US")}`;
  const kinds = [...new Set(on.map((p) => p.groupType))];
  if (kinds.length === 1) {
    const first = findGroup(groupsByType, on[0]!.groupType, on[0]!.code);
    const name = first ? chipLabel(first) : on[0]!.code;
    const more = on.length > 1 ? ` + ${on.length - 1}` : "";
    return `${kindLabel(kinds[0]!)} › ${name}${more}${tail}`;
  }
  return `${on.length} groups (${selection.combine.toUpperCase()})${tail}`;
}

/** The picker's synthetic first kind, listing every group. */
export const ALL_KINDS = "*";

/** One row of the picker's first step. */
export interface KindRow {
  groupType: string;
  label: string;
  /** Groups in the kind; null for All, which the mock leaves blank. */
  count: number | null;
  /** How many of its groups are picked — shown instead of the count. */
  picked: number;
}

export function kindRows(types: readonly SymbolGroupType[], selection: GroupSelection): KindRow[] {
  return [
    { groupType: ALL_KINDS, label: "All", count: null, picked: selection.picked.length },
    ...types.map((t) => ({
      groupType: t.groupType,
      label: t.label,
      count: t.groupCount,
      picked: selection.picked.filter((p) => p.groupType === t.groupType).length,
    })),
  ];
}

// ── Shape ─────────────────────────────────────────────────────────────────

/** Under this the bar stacks whatever side it is docked on. */
export const TALL_BELOW = 700;

/** From this width `▦` carries its word (plan, decision 5). */
export const COLUMNS_WORD_FROM = 1000;

/**
 * Whether the bar stacks into rows.
 *
 * The dock side decides it when there is one — a screener docked left or right
 * is a column, and stacks. Top, bottom or no dock is one row unless the panel
 * is narrow, which is also the phone-width answer. A width of 0 is "not laid
 * out yet" and decides nothing.
 */
export function isTall(side: ScreenerPlacement | undefined, width: number): boolean {
  if (side === "left" || side === "right") return true;
  return width > 0 && width < TALL_BELOW;
}
